import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useToast } from "@/hooks/use-toast";
import { signupSchema, SignupFormValues } from "./schema/signupSchema";
import { PasswordInputField } from "./password/PasswordInputField";
import { TermsCheckbox } from "./terms/TermsCheckbox";
import { SubmitButton } from "./buttons/SubmitButton";
import { CountrySelector } from "./CountrySelector";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, CheckCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { trackEvent } from "@/services/analytics";
import { initializeCurrencyFromCountry } from "@/utils/currency";

export const SignupForm: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [userEmail, setUserEmail] = useState<string>("");
  const [isResending, setIsResending] = useState(false);
  const [countdown, setCountdown] = useState<number>(10);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  
  // Get invitation token from URL if present
  const invitationToken = searchParams.get('invitation_token');
  
  // Store invitation token in localStorage for persistence through email verification
  useEffect(() => {
    if (invitationToken) {
      localStorage.setItem('pending_invitation_token', invitationToken);
    }
  }, [invitationToken]);

  const form = useForm<SignupFormValues>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      email: "",
      password: "",
      confirmPassword: "",
      country: undefined,
      acceptTerms: false,
    },
  });

  // Handle countdown timer after successful signup
  useEffect(() => {
    if (success && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (success && countdown === 0) {
      // Preserve invitation token in redirect
      const pendingToken = localStorage.getItem('pending_invitation_token');
      if (pendingToken) {
        navigate(`/login?signup=success&invitation_token=${pendingToken}`);
      } else {
        navigate("/login?signup=success");
      }
    }
  }, [success, countdown, navigate]);

  const checkIfEmailExists = async (email: string) => {
    const { data, error } = await supabase
      .from("profiles")
      .select("*")
      .eq("email", email);
    if (error) {
      console.error("Error checking email:", error);
      return false;
    }
    return data.length > 0;
  };

  const handleSignup = async (values: SignupFormValues) => {
    setIsLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const emailExists = await checkIfEmailExists(values.email);
      if (emailExists) {
        throw new Error(
          "An account with this email already exists. Please try logging in instead."
        );
      }
      console.log(
        "Attempting signup with:",
        values.email,
        "Country:",
        values.country
      );

      // Create user - Supabase will handle duplicate email detection automatically
      // Redirect to login page after email verification, preserving invitation token
      const pendingInvitationToken = localStorage.getItem('pending_invitation_token');
      let redirectUrl = `${window.location.origin}/login?verified=true`;
      if (pendingInvitationToken) {
        redirectUrl += `&invitation_token=${pendingInvitationToken}`;
      }
      
      const { data, error } = await supabase.auth.signUp({
        email: values.email,
        password: values.password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            country: values.country,
          },
        },
      });
      console.log("Supabase signup response:", data, error);
      if (error) {
        console.error("Supabase signup error:", error.message);
        console.error("Supabase signup error:", error.stack);

        // Handle specific error cases
        if (
          error.message?.includes("User already registered") ||
          error.message?.includes("already registered") ||
          error.message?.includes("already exists")
        ) {
          throw new Error(
            "An account with this email already exists. Please try logging in instead."
          );
        }

        throw error;
      }

      // Check if user was created successfully
      if (data.user && !data.user.email_confirmed_at) {
        console.log("User created successfully:", data.user.id);

        // Initialize currency based on selected country
        if (values.country) {
          try {
            initializeCurrencyFromCountry(values.country, data.user.id);
            localStorage.setItem(`userCountry_${data.user.id}`, values.country);
          } catch (currencyError) {
            console.error("Error initializing currency:", currencyError);
          }
        }

        // Show success message
        setSuccess(true);
        setUserEmail(values.email);
        setCountdown(10); // Reset countdown to 10 seconds
        form.reset();

        // Track successful signup
        trackEvent("sign_up", "auth", "email_signup", 1);

        toast({
          title: "Account created successfully",
          description:
            "Please check your email for a verification link from Proptpilot to complete your registration.",
        });

        console.log("Signup process completed successfully");
      } else if (data.user && data.user.email_confirmed_at) {
        // User already exists and is confirmed
        throw new Error(
          "An account with this email already exists. Please try logging in instead."
        );
      } else {
        console.error("No user data returned from signup");
        throw new Error("Signup failed. Please try again.");
      }
    } catch (err: any) {
      console.error("Signup error:", err);

      // Track failed signup
      trackEvent("sign_up_failed", "auth", err?.message || "unknown_error");

      // Set user-friendly error message
      const errorMessage =
        err?.message || "An unexpected error occurred. Please try again.";
      setError(errorMessage);

      toast({
        title: "Signup failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-card/95 backdrop-blur-sm p-6 rounded-lg shadow-xl border">
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="mb-4 bg-blue-50 border-blue-200 text-blue-800">
          <CheckCircle className="h-4 w-4 text-blue-600" />
          <AlertDescription>
            <strong>Welcome to Proptpilot!</strong>
            <br />
            Please check your email from Proptpilot and click the verification
            link. You'll be automatically logged in after verification.
            <br />
            <em>
              You will be redirected to the login page in {countdown} second
              {countdown !== 1 ? "s" : ""}...
            </em>
          </AlertDescription>
        </Alert>
      )}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSignup)} className="space-y-6">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder="you@example.com"
                    {...field}
                    disabled={isLoading || success}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="country"
            render={({ field }) => (
              <CountrySelector field={field} isLoading={isLoading || success} />
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Password</FormLabel>
                <FormControl>
                  <PasswordInputField
                    field={field}
                    placeholder="••••••••"
                    isLoading={isLoading || success}
                    autoComplete="new-password"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Confirm Password</FormLabel>
                <FormControl>
                  <PasswordInputField
                    field={field}
                    placeholder="••••••••"
                    isLoading={isLoading || success}
                    autoComplete="new-password"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <TermsCheckbox form={form} isLoading={isLoading || success} />

          <SubmitButton isLoading={isLoading} disabled={success} />

          {!success && (
            <div className="text-center text-sm">
              <span>
                Already have an account?{" "}
                <Link to="/login" className="text-primary hover:underline">
                  Sign in
                </Link>
              </span>
            </div>
          )}
        </form>
      </Form>
    </div>
  );
};
